// Hook para cargar datos asíncronos con estados loading/error/data y reintento
// (degradación controlada con conexión limitada — Req. 17.4, 18.4).

import { useCallback, useEffect, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Vuelve a ejecutar la carga. */
  reload: () => void;
}

/** Ejecuta `loader` al montar y cuando cambian las `deps`; expone reintento. */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    loader()
      .then((result) => {
        if (active) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(err instanceof Error ? err.message : 'Error inesperado.');
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
    // `deps` es variádico (spread del llamador): la regla no puede verificar
    // estáticamente un arreglo con spread, por eso se desactiva en esta línea.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);

  return { data, loading, error, reload };
}
